import { getStatusIcon } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/coach-partnership-status';
import type {
  CoachingPartnershipStatus,
  CoachProfile,
} from '@efdigital/ll-addon-data';
import {
  coachCurrentRole,
  coachDisplayName,
  coachLocations,
  formatTimezoneDisplay,
} from '@efdigital/ll-xceed-v2/utils/coaching/coach-profile-display';
import type { TOC } from '@ember/component/template-only';

interface CoachProfileHeaderSignature {
  Args: {
    coach: CoachProfile;
    partnershipStatus?: CoachingPartnershipStatus;
    hasChemistrySession?: boolean;
  };
  Blocks: {
    actions: [];
  };
  Element: HTMLDivElement;
}

function truthy(value?: boolean): boolean {
  return typeof value === 'boolean' && value;
}

export const CoachProfileHeader = <template>
  <div class="card border shadow-sm rounded-4" ...attributes>
    <div class="card-body p-4 p-lg-5">
      <div class="d-flex flex-column flex-md-row gap-4 align-items-md-start">
        <div class="flex-grow-1">
          <h1 class="h3 fw-bold mb-1">{{coachDisplayName @coach}}</h1>
          {{#let (coachCurrentRole @coach) as |currentRole|}}
            {{#if currentRole}}
              <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
                <p class="text-primary fw-semibold mb-0">{{currentRole}}</p>
              </div>
            {{/if}}
          {{/let}}

          <ul
            class="list-unstyled d-flex flex-wrap align-items-center gap-3 mb-0 small text-body-secondary fw-semibold"
          >
            {{#let (coachLocations @coach) as |locations|}}
              {{#if locations}}
                <li class="d-inline-flex align-items-center gap-2">
                  <iconify-icon inline icon="ph:map-pin" aria-hidden="true" />
                  {{locations}}
                </li>
              {{/if}}
            {{/let}}
            {{#let (formatTimezoneDisplay @coach.timezone) as |timezone|}}
              {{#if timezone}}
                <li class="d-inline-flex align-items-center gap-2">
                  <iconify-icon inline icon="ph:clock" aria-hidden="true" />
                  {{timezone.name}}
                  {{#if timezone.offset}}
                    ·
                    {{timezone.offset}}
                  {{/if}}
                </li>
              {{/if}}
            {{/let}}
            {{#if @coach.linkedInProfileLink}}
              <li class="d-inline-flex align-items-center gap-2">
                <iconify-icon
                  inline
                  icon="ph:linkedin-logo"
                  aria-hidden="true"
                />
                <a
                  href={{@coach.linkedInProfileLink}}
                  class="link-primary link-underline-opacity-0"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
              </li>
            {{/if}}
            {{#if @partnershipStatus}}
              <li class="d-inline-flex align-items-center gap-2">
                {{#let
                  (getStatusIcon
                    @partnershipStatus (truthy @hasChemistrySession)
                  )
                  as |icon|
                }}
                  {{#if icon}}
                    <span
                      class="px-3 rounded-pill shadow-sm text-bg-{{icon.color}}"
                    >
                      <iconify-icon icon={{icon.icon}} inline />
                      {{icon.label}}
                    </span>
                  {{/if}}
                {{/let}}
              </li>
            {{/if}}
          </ul>
        </div>

      </div>
      {{yield to="actions"}}
    </div>
  </div>
</template> satisfies TOC<CoachProfileHeaderSignature>;
